import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  Chip,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  InputAdornment,
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 600,
  },
  form: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2),
    },
  },
  rateSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  infoIcon: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  tableSection: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  inputAdornment: {
    whiteSpace: 'nowrap',
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  chemicalCard: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  summarySection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  deleteBlendButton: {
    marginRight: 'auto',
    color: theme.palette.error.main,
  },
}));

// Constants
const DEFAULT_CALIBRATION_RATE = 20; // gal/acre
const SITE_OPTIONS = ['Ornamental/Right-of-Way', 'Turf', 'Bare Ground'];
const SEASONS = [
  { value: 'Warm', label: 'Warm Season' },
  { value: 'Cold', label: 'Cold Season' },
  { value: 'All', label: 'All Seasons' },
];
const TYPES = [
  { value: 'Pre Emergent', label: 'Pre Emergent' },
  { value: 'Post', label: 'Post' },
  { value: 'All', label: 'All' },
];
const SPOT_UNITS = [
  { value: 'oz', label: 'oz' },
  { value: 'gal', label: 'gal' },
];
const SPOT_PER = [
  { value: 'gal', label: 'per gal' },
  { value: '100gal', label: 'per 100 gal' },
];
const BROADCAST_UNITS = [
  { value: 'oz', label: 'oz' },
  { value: 'gal', label: 'gal' },
];
const BROADCAST_PER = [
  { value: 'acre', label: 'per acre' },
  { value: '1000sqft', label: 'per 1000 sq ft' },
];

// Helper functions
const isValidRate = (rate) => {
  const num = parseFloat(rate);
  return !isNaN(num) && num > 0;
};

const formatRate = (rate) => {
  return rate ? `${rate.rate}${rate.unit}/${rate.per}` : '-';
};

const ChemicalBlendModal = ({ 
  open, 
  onClose, 
  availableChemicals, 
  onSave, 
  onUpdate, 
  onDelete,
  editMode = false, 
  existingBlend = null 
}) => {
  const classes = useStyles();

  // Initialize form state based on mode
  const getInitialState = () => {
    console.log('get initial state', editMode, existingBlend)
    if (editMode && existingBlend) {
      
      return {
        ...existingBlend,
        // Ensure all fields exist
        name: existingBlend.name || '',
        season: existingBlend.season || '',
        type: existingBlend.type || '',
        applicationAreas: existingBlend.applicationAreas || [],
        chemicals: existingBlend.chemicals || [],
        isHerbicide: existingBlend.isHerbicide !== undefined ? existingBlend.isHerbicide : true,
        calibrationRate: existingBlend.calibrationRate || DEFAULT_CALIBRATION_RATE,
        active: existingBlend.active !== undefined ? existingBlend.active : true,
      };
    }
    
    return {
      name: '',
      season: '',
      type: '',
      applicationAreas: [],
      chemicals: [],
      isHerbicide: true,
      calibrationRate: DEFAULT_CALIBRATION_RATE,
      active: true,
    };
  };

  // Form state
  const [blendData, setBlendData] = useState(getInitialState());
  
  // Reset form when modal opens/closes or when switching between modes
  useEffect(() => {
    if (open) {
      setBlendData(getInitialState());
      setErrors({
        name: '',
        season: '',
        type: '',
        applicationAreas: '',
        chemicals: '',
        spotRate: '',
        broadcastRate: '',
      });
    }
  }, [open, editMode, existingBlend]);
  
  // Current chemical being added
  const [currentChemical, setCurrentChemical] = useState({
    chemical: null,
    spotRate: '',
    spotUnit: 'oz',
    spotPer: 'gal',
    broadcastRate: '',
    broadcastUnit: 'oz',
    broadcastPer: 'acre',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({
    name: '',
    season: '',
    type: '',
    applicationAreas: '',
    chemicals: '',
    spotRate: '',
    broadcastRate: '',
  });

  // Reset all form state
  const resetForm = () => {
    setBlendData(getInitialState());
    setCurrentChemical({
      chemical: null,
      spotRate: '',
      spotUnit: 'oz',
      spotPer: 'gal',
      broadcastRate: '',
      broadcastUnit: 'oz',
      broadcastPer: 'acre',
    });
    setErrors({
      name: '',
      season: '',
      type: '',
      applicationAreas: '',
      chemicals: '',
      spotRate: '',
      broadcastRate: '',
    });
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Validate a single field
  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return value ? '' : 'Blend name is required';
      case 'season':
        return value ? '' : 'Season is required';
      case 'type':
        return value ? '' : 'Type is required';
      case 'applicationAreas':
        return value.length > 0 ? '' : 'At least one application area is required';
      case 'chemicals':
        return value.length > 0 ? '' : 'At least one chemical is required';
      case 'spotRate':
        return value === '' || isValidRate(value) ? '' : 'Rate must be greater than 0';
      case 'broadcastRate':
        return value === '' || isValidRate(value) ? '' : 'Rate must be greater than 0';
      default:
        return '';
    }
  };

  // Update a field with validation
  const updateField = (field, value) => {
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
    setBlendData(prev => ({ ...prev, [field]: value }));
  };

  // Update current chemical field with validation
  const updateChemicalField = (field, value) => {
    const error = field === 'spotRate' || field === 'broadcastRate' 
      ? validateField(field, value)
      : '';
    
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    setCurrentChemical(prev => ({ ...prev, [field]: value }));
  };

  // Add chemical to blend
  const addChemical = () => {
    if (!currentChemical.chemical) return;
    
    // Validate rates
    const spotRateError = validateField('spotRate', currentChemical.spotRate);
    const broadcastRateError = validateField('broadcastRate', currentChemical.broadcastRate);
    
    if (spotRateError) {
      setErrors(prev => ({ ...prev, spotRate: spotRateError }));
      return;
    }
    
    if (broadcastRateError) {
      setErrors(prev => ({ ...prev, broadcastRate: broadcastRateError }));
      return;
    }
    
    // Ensure at least one rate is provided
    if (!currentChemical.spotRate && !currentChemical.broadcastRate) {
      setErrors(prev => ({ 
        ...prev, 
        spotRate: 'At least one rate type is required',
        broadcastRate: 'At least one rate type is required'
      }));
      return;
    }
    
    const newChemical = {
      chemicalId: currentChemical.chemical._id,
      name: currentChemical.chemical.name,
      spot: currentChemical.spotRate ? {
        rate: parseFloat(currentChemical.spotRate),
        unit: currentChemical.spotUnit,
        per: currentChemical.spotPer
      } : null,
      broadcast: currentChemical.broadcastRate ? {
        rate: parseFloat(currentChemical.broadcastRate),
        unit: currentChemical.broadcastUnit,
        per: currentChemical.broadcastPer,
        // Store original rate for calibration adjustments
        originalRate: parseFloat(currentChemical.broadcastRate)
      } : null
    };

    const updatedChemicals = [...blendData.chemicals, newChemical];
    
    setBlendData(prev => ({
      ...prev,
      chemicals: updatedChemicals
    }));
    
    // Clear chemicals error if it exists
    if (errors.chemicals) {
      setErrors(prev => ({ ...prev, chemicals: '' }));
    }

    // Reset current chemical
    setCurrentChemical({
      chemical: null,
      spotRate: '',
      spotUnit: 'oz',
      spotPer: 'gal',
      broadcastRate: '',
      broadcastUnit: 'oz',
      broadcastPer: 'acre',
    });
    
    // Clear rate errors
    setErrors(prev => ({ 
      ...prev, 
      spotRate: '',
      broadcastRate: ''
    }));
  };

  // Remove chemical from blend
  const removeChemical = (index) => {
    const updatedChemicals = blendData.chemicals.filter((_, i) => i !== index);
    
    setBlendData(prev => ({
      ...prev,
      chemicals: updatedChemicals
    }));
    
    // Set chemicals error if list becomes empty
    if (updatedChemicals.length === 0) {
      setErrors(prev => ({ ...prev, chemicals: 'At least one chemical is required' }));
    }
  };

  // Adjust broadcast rates when calibration rate changes
  const handleCalibrationRateChange = (newRate) => {
    const parsedRate = parseFloat(newRate) || DEFAULT_CALIBRATION_RATE;
    
    // Update chemicals with adjusted broadcast rates
    const adjustedChemicals = blendData.chemicals.map(chem => {
      if (!chem.broadcast) return chem;
      
      const originalRate = chem.broadcast.originalRate || chem.broadcast.rate;
      const adjustmentFactor = parsedRate / DEFAULT_CALIBRATION_RATE;
      
      return {
        ...chem,
        broadcast: {
          ...chem.broadcast,
          originalRate: originalRate,
          rate: parseFloat((originalRate * adjustmentFactor).toFixed(2))
        }
      };
    });
    
    setBlendData(prev => ({
      ...prev,
      calibrationRate: parsedRate,
      chemicals: adjustedChemicals
    }));
  };

  // Validate entire form before saving
  const validateForm = () => {
    const formErrors = {
      name: validateField('name', blendData.name),
      season: validateField('season', blendData.season),
      type: validateField('type', blendData.type),
      applicationAreas: validateField('applicationAreas', blendData.applicationAreas),
      chemicals: validateField('chemicals', blendData.chemicals),
    };
    
    setErrors(formErrors);
    
    // Check if there are any errors
    return !Object.values(formErrors).some(error => error !== '');
  };

  // Save/Update blend
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    
    const blendToSave = {
      ...blendData,
      // For new blends, construct the name
      name: editMode 
        ? blendData.name 
        : `${blendData.season} ${blendData.type} Blend - ${blendData.name}`,
    };
    
    if (editMode && onUpdate) {
      onUpdate(blendToSave);
    } else if (onSave) {
      onSave(blendToSave);
    }
    
    // handleClose();
  };

  // Handle delete (only in edit mode)
  const handleDelete = () => {
    if (editMode && onDelete && existingBlend?._id) {
      if (window.confirm('Are you sure you want to delete this blend?')) {
        onDelete(existingBlend._id);
        handleClose();
      }
    }
  };

  // Check if form is valid (for disabling save button)
  const isFormValid = () => {
    return (
      blendData.name &&
      blendData.season &&
      blendData.type &&
      blendData.applicationAreas.length > 0 &&
      blendData.chemicals.length > 0
    );
  };

  // Check if current chemical form is valid (for disabling add button)
  const isChemicalFormValid = () => {
    return (
      currentChemical.chemical &&
      (currentChemical.spotRate || currentChemical.broadcastRate) &&
      (!currentChemical.spotRate || isValidRate(currentChemical.spotRate)) &&
      (!currentChemical.broadcastRate || isValidRate(currentChemical.broadcastRate))
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogContent }}
    >
      <DialogTitle>
        <Typography variant="h5">
          {editMode ? 'Edit Chemical Blend' : 'Create Chemical Blend'}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <form className={classes.form} noValidate>
          {/* Basic Blend Information */}
          <Typography variant="h6" gutterBottom>Blend Information</Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Blend Name"
                value={blendData.name}
                onChange={(e) => updateField('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth error={!!errors.season} required>
                <InputLabel>Season</InputLabel>
                <Select
                  value={blendData.season}
                  onChange={(e) => updateField('season', e.target.value)}
                >
                  {SEASONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.season && <FormHelperText>{errors.season}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth error={!!errors.type} required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={blendData.type}
                  onChange={(e) => updateField('type', e.target.value)}
                >
                  {TYPES.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.applicationAreas} required>
                <Autocomplete
                  multiple
                  id="applicationAreas-select"
                  options={SITE_OPTIONS}
                  value={blendData.applicationAreas}
                  onChange={(event, newValue) => updateField('applicationAreas', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Application Areas"
                      placeholder="Select areas"
                      error={!!errors.applicationAreas}
                      helperText={errors.applicationAreas}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        className={classes.chip}
                      />
                    ))
                  }
                />
              </FormControl>
            </Grid>
            
            {/* Status controls (for edit mode) */}
            {editMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={blendData.isHerbicide}
                      onChange={(e) => updateField('isHerbicide', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Is Herbicide"
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={blendData.active}
                      onChange={(e) => updateField('active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
          </Grid>

          <Divider className={classes.divider} />
          {/* Blend Summary */}
          {blendData.chemicals.length > 0 && (
            <div className={classes.summarySection}>
              <Typography variant="subtitle1" gutterBottom>
                Blend Summary
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {editMode ? blendData.name : `${blendData.season} ${blendData.type} Blend - ${blendData.name}`}
              </Typography>
              <Typography variant="body2">
                <strong>Application Areas:</strong> {blendData.applicationAreas.join(', ')}
              </Typography>
              <Typography variant="body2">
                <strong>Chemical Count:</strong> {blendData.chemicals.length}
              </Typography>
              <Typography variant="body2">
                <strong>Calibration Rate:</strong> {blendData.calibrationRate} gal/acre
              </Typography>
              {editMode && (
                <Typography variant="body2">
                  <strong>Status:</strong> {blendData.active ? 'Active' : 'Inactive'}
                </Typography>
              )}
            </div>
          )}

                    
          {blendData.chemicals.length > 0 && (
            <div className={classes.tableSection}>
              <Typography variant="h6" gutterBottom>Blend Contents</Typography>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Chemical</TableCell>
                      <TableCell>Spot Rate</TableCell>
                      <TableCell>Broadcast Rate</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blendData.chemicals.map((chem, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{chem.name}</TableCell>
                        <TableCell>
                          {formatRate(chem.spot)}
                        </TableCell>
                        <TableCell>
                          {formatRate(chem.broadcast)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => removeChemical(idx)}
                            className={classes.deleteButton}
                            aria-label="Remove chemical"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
          
          {/* Chemical Selection */}
          <Typography variant="h6" gutterBottom>Chemical Selection</Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                id="chemical-select"
                options={availableChemicals}
                getOptionLabel={(option) => option.name}
                value={currentChemical.chemical}
                onChange={(event, newValue) => updateChemicalField('chemical', newValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Chemical"
                    helperText="Choose a chemical to add to this blend"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Spot Application Section */}
          <Card className={classes.chemicalCard}>
            <CardContent>
              <div className={classes.rateSection}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Spot Spray Rate
                  <Tooltip title="Spot spray is a concentration-based application method for targeted treatments. Specify how much chemical to mix per volume of water.">
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Rate"
                      value={currentChemical.spotRate}
                      onChange={(e) => updateChemicalField('spotRate', e.target.value)}
                      error={!!errors.spotRate}
                      helperText={errors.spotRate || "Amount of chemical"}
                      InputProps={{
                        endAdornment: currentChemical.spotUnit ? (
                          <InputAdornment position="end" className={classes.inputAdornment}>
                            {currentChemical.spotUnit}
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={currentChemical.spotUnit}
                        onChange={(e) => updateChemicalField('spotUnit', e.target.value)}
                      >
                        {SPOT_UNITS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Per</InputLabel>
                      <Select
                        value={currentChemical.spotPer}
                        onChange={(e) => updateChemicalField('spotPer', e.target.value)}
                      >
                        {SPOT_PER.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </div>

              {/* Broadcast Application Section */}
              <div className={classes.rateSection}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Broadcast Rate
                  <Tooltip title="Broadcast application is an area-based application method for treating entire surfaces. Specify how much chemical to apply per unit area.">
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Rate"
                      value={currentChemical.broadcastRate}
                      onChange={(e) => updateChemicalField('broadcastRate', e.target.value)}
                      error={!!errors.broadcastRate}
                      helperText={errors.broadcastRate || "Amount of chemical"}
                      InputProps={{
                        endAdornment: currentChemical.broadcastUnit ? (
                          <InputAdornment position="end" className={classes.inputAdornment}>
                            {currentChemical.broadcastUnit}
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={currentChemical.broadcastUnit}
                        onChange={(e) => updateChemicalField('broadcastUnit', e.target.value)}
                      >
                        {BROADCAST_UNITS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Per</InputLabel>
                      <Select
                        value={currentChemical.broadcastPer}
                        onChange={(e) => updateChemicalField('broadcastPer', e.target.value)}
                      >
                        {BROADCAST_PER.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </div>

              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={addChemical}
                  disabled={!isChemicalFormValid()}
                  className={classes.addButton}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Add Chemical to Blend
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Calibration Rate Section */}
          <div className={classes.rateSection} style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Equipment Calibration
              <Tooltip title="The calibration rate determines how much spray solution is applied per acre. Changing this value will automatically adjust the broadcast application rates.">
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            </Typography>
            
            <TextField
              fullWidth
              type="number"
              label="Calibration Rate"
              value={blendData.calibrationRate}
              onChange={(e) => handleCalibrationRateChange(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">gal/acre</InputAdornment>,
              }}
              helperText={`Default rate is ${DEFAULT_CALIBRATION_RATE} gal/acre. Adjust based on your equipment calibration.`}
            />
          </div>

          {/* Chemicals Table */}
          {errors.chemicals && (
            <Typography className={classes.error}>{errors.chemicals}</Typography>
          )}



        </form>
      </DialogContent>
      
      <DialogActions>
        {editMode && (
          <Button 
            onClick={handleDelete} 
            color="secondary"
            className={classes.deleteBlendButton}
            startIcon={<DeleteIcon />}
          >
            Delete Blend
          </Button>
        )}
        <Button 
          onClick={handleClose} 
          color="default"
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={!isFormValid()}
          startIcon={editMode ? <EditIcon /> : <SaveIcon />}
        >
          {editMode ? 'Update Blend' : 'Save Blend'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChemicalBlendModal;
